import React, { useState, useEffect } from 'react';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { adminHubAuth } from '../../config/firebase.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { getFirebaseErrorMessage } from '../../utils/firebaseErrors.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminLoginPage.css';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

/**
 * 管理者専用ログイン（Google アカウントのみ）
 */
const AdminLoginPage = ({ authError = '', onClearAuthError }) => {
  const probe = useScreenProbe('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    probe.unlock();
  }, [probe]);

  useEffect(() => {
    if (authError) {
      setError(authError);
      setLoading(false);
    }
  }, [authError]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(adminHubAuth, (authUser) => {
      if (!authUser) {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const handleGoogleSignIn = async () => {
    setError('');
    onClearAuthError?.();
    setLoading(true);

    try {
      await signInWithPopup(adminHubAuth, googleProvider);
    } catch (err) {
      console.error('Google ログインエラー:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('ログインがキャンセルされました。');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('別の認証方式で登録されたアカウントです。Google でログインしてください。');
      } else {
        setError(getFirebaseErrorMessage(err));
      }
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <img src="/logo.webp" alt="Synapse" className="admin-login-logo" />
          <h1>Synapse Admin Hub</h1>
          <p>許可された Google アカウントでログインしてください</p>
        </div>

        {error && <div className="admin-login-error">{error}</div>}

        <button
          type="button"
          className="btn btn-primary admin-login-submit admin-login-google"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          {loading ? (
            <>
              <LoadingSpinner size="small" />
              <span>ログイン中...</span>
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Google でログイン</span>
            </>
          )}
        </button>

        <p className="admin-login-note">
          アクセスには管理者による Google アカウントの事前許可が必要です。
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
